// Turns /staffing/summary's per-cost-center rows into the selector/Summary
// entries the Staffing Tracker works with: Admin (100-125) and SBC (500-599)
// each collapse into one entry, every other cost center stands alone. Which
// cost center belongs where is decided server-side (staffingConfig.js
// costCenterGroup) and arrives on each row as group_key / group_label / region.

export const REGION_ORDER = ['West', 'East', 'Administration', 'Other'];
export const ADMIN_GROUP_KEY = 'group:admin';

// Vacancy-rate colouring on the Summary, taken from the Excel tracker it
// replaces: green at or under 2%, yellow above 2% and under 3%, red from 3%.
export const RATE_YELLOW_ABOVE = 2;
export const RATE_RED_FROM = 3;

const SUM_FIELDS = [
  'headcount', 'vacancy_count', 'hc_driver', 'hc_mechanic', 'vac_driver', 'vac_mechanic',
  'new_hires', 'nh_driver', 'nh_mechanic', 'filled_count', 'days_to_fill_sum',
];

const round1 = (n) => Math.round(n * 10) / 10;

// Adds the derived columns to a bag of summed counts. Operations is whatever
// isn't a Driver or Mechanic -- many Operations job profiles (Helpers,
// Dispatchers, Traffic Controllers, ...) carry no category at all, so counting
// only the mapped ones would leave the three position columns short of Current.
export function deriveMetrics(sums) {
  const total = sums.headcount + sums.vacancy_count;
  const mechTotal = sums.hc_mechanic + sums.vac_mechanic;
  return {
    ...sums,
    total_headcount: total,
    hc_operations: sums.headcount - sums.hc_driver - sums.hc_mechanic,
    nh_operations: sums.new_hires - sums.nh_driver - sums.nh_mechanic,
    vac_operations: sums.vacancy_count - sums.vac_driver - sums.vac_mechanic,
    vacancy_rate: total > 0 ? round1((sums.vacancy_count / total) * 100) : 0,
    mechanic_vacancy_rate: mechTotal > 0 ? round1((sums.vac_mechanic / mechTotal) * 100) : 0,
    avg_days_open: sums.vacancy_count > 0 ? round1(sums.open_days_sum / sums.vacancy_count) : 0,
    days_to_fill: sums.filled_count > 0 ? round1(sums.days_to_fill_sum / sums.filled_count) : null,
  };
}

const zeroSums = () => ({ ...Object.fromEntries(SUM_FIELDS.map((f) => [f, 0])), open_days_sum: 0 });

function addRow(sums, row) {
  for (const f of SUM_FIELDS) sums[f] += Number(row[f] || 0);
  sums.open_days_sum += Number(row.avg_days_open || 0) * Number(row.vacancy_count || 0);
}

// Sum of any set of entries/rows, with the derived columns recomputed from the
// sums -- a rate of rates would not match the table it totals.
export function rollup(rows) {
  const sums = zeroSums();
  rows.forEach((r) => addRow(sums, r));
  return deriveMetrics(sums);
}

export function buildCcEntries(byCostCenter) {
  const map = new Map();
  for (const row of byCostCenter || []) {
    let entry = map.get(row.group_key);
    if (!entry) {
      entry = {
        key: row.group_key,
        label: row.group_label || `${row.code} — ${row.name}`,
        region: row.region,
        isAdmin: row.group_key === ADMIN_GROUP_KEY,
        ccIds: [],
        minCode: Number(row.code) || 0,
        sums: zeroSums(),
      };
      map.set(row.group_key, entry);
    }
    entry.ccIds.push(row.cc_id);
    entry.minCode = Math.min(entry.minCode, Number(row.code) || entry.minCode);
    addRow(entry.sums, row);
  }
  return [...map.values()]
    .map(({ sums, ...entry }) => ({ ...entry, ...deriveMetrics(sums) }))
    .sort((a, b) => (REGION_ORDER.indexOf(a.region) - REGION_ORDER.indexOf(b.region)) || (a.minCode - b.minCode));
}
